import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { StyledPageTitle } from 'theme/styles';
import { useTranslations } from 'hooks/useTranslations';
import { useDictionaries } from 'pages/DictionariesPage/hooks';
import { useAlert } from 'models/alertContext';
import { DictionaryViewTableCard } from 'pages/DictionariesPage/ViewDictionaryPage/DictionaryViewTableCard';
import { Alert } from 'components/atoms/Alert';
import { Table } from 'components/molecules/Table';
import { CardList } from 'components/molecules/CardList';
import { Pagination } from 'components/molecules/Pagination';
import { EPerPages, ESortDirectionTypes, ISortOptions } from 'models/table';
import { ActionTypes } from 'state/actions/alert';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { dictionaryViewTableColumns, mapDictionaryViewToDataSource } from '../helpers';
import { Typography } from '@mui/material';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { colors } from 'theme/colors';

interface IIconCheck {
  checked: boolean;
}

const IconCheck = ({ checked }: IIconCheck) => {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: '18px',
        height: '18px',
        marginTop: '1px',
        color: checked ? colors.success700 : colors.error700,
        border: `2px solid ${checked ? colors.success700 : colors.error700}`,
        borderRadius: '25px',
      }}
    >
      <Icon
        type={checked ? EIconTypes.checkmark : EIconTypes.close}
        fontSize={14}
        color={checked ? colors.success700 : colors.error700}
      />
    </span>
  );
};

const ViewDictionaryPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { type } = useParams<{ type: EDictionaryTypes }>();
  const { dictionaryAlert, nextPageAlert, clearAlert } = useAlert();
  const { dictionary, dictionaryValues, getDictionaryData, paginator } = useDictionaries();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [sort, setSort] = useState<ISortOptions>({
    property: 'name',
    direction: ESortDirectionTypes.ASC,
  });
  const { isDesktop } = useThemeBreakpoint();
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  useEffect(() => {
    if (type) {
      (async function getDictionariesDataInit() {
        setIsPaginationLoading(true);
        await getDictionaryData(type, currentPage, perPage, sort);
        setIsPaginationLoading(false);
      })();
    }
  }, [type, currentPage, perPage, sort, selectedLanguage]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_DICTIONARY_ALERT);
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          { label: t('dictionaries.header'), route: ERouteLinks.Dictionaries },
          `${t('dictionary.header')} ${t('dictionaries.dictionariesTable.view.link')}`,
        ]}
      />
      <StyledPageTitle variant="h4">
        {`${dictionary?.translatedType || t('dictionary.header')}`}
      </StyledPageTitle>

      <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('dictionaries.dictionaryViewTable.description.label')}
          </Typography>
          <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
            {dictionary?.translatedDescription}
          </Typography>
        </Grid>
        <Grid container size={{ xs: 12 }} spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('dictionaries.dictionaryViewTable.manageable.label')}
            </Typography>
            <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
              {dictionary?.managedByAdmin ? t('general.yes.button') : t('general.no.button')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('dictionaries.dictionaryViewTable.translations.label')}
            </Typography>
            <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
              {dictionary?.translatedValues ? t('general.yes.button') : t('general.no.button')}
            </Typography>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }} spacing={2}>
          <Grid container spacing={1} size={{ xs: 12, md: 6 }}>
            <Grid container spacing={1} wrap="nowrap" size={{ xs: 12 }}>
              <IconCheck checked={!!dictionary?.newValues} />
              <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
                {t('dictionaries.dictionaryViewTable.values.canAddNew.text').replace(
                  '{0}',
                  dictionary?.newValues
                    ? t('dictionaries.dictionaryViewTable.can.text')
                    : t('dictionaries.dictionaryViewTable.cannot.text'),
                )}
              </Typography>
            </Grid>
            <Grid container spacing={1} wrap="nowrap" size={{ xs: 12 }}>
              <IconCheck checked={!!dictionary?.valuesDeactivation} />
              <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
                {t(
                  'dictionaries.dictionaryViewTable.values.canActivateAndDeactivate.text',
                ).replace(
                  '{0}',
                  dictionary?.valuesDeactivation
                    ? t('dictionaries.dictionaryViewTable.can.text')
                    : t('dictionaries.dictionaryViewTable.cannot.text'),
                )}
              </Typography>
            </Grid>
            <Grid container spacing={1} wrap="nowrap" size={{ xs: 12 }}>
              <IconCheck checked={!!dictionary?.englishValuesNamesUpdate} />
              <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
                {t(
                  'dictionaries.dictionaryViewTable.values.canUpdateEnglishNames.text',
                ).replace(
                  '{0}',
                  dictionary?.englishValuesNamesUpdate
                    ? t('dictionaries.dictionaryViewTable.can.text')
                    : t('dictionaries.dictionaryViewTable.cannot.text'),
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={1} size={{ xs: 12, md: 6 }}>
            <Grid container spacing={1} wrap="nowrap" size={{ xs: 12 }}>
              <IconCheck checked={!!dictionary?.translatedValues} />
              <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
                {t('dictionaries.dictionaryViewTable.values.canTranslate.text').replace(
                  '{0}',
                  dictionary?.translatedValues
                    ? t('dictionaries.dictionaryViewTable.can.text')
                    : t('dictionaries.dictionaryViewTable.cannot.text'),
                )}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {type && dictionary?.managedByAdmin && (
        <Grid container sx={{ justifyContent: 'end' }}>
          <Grid sx={{ marginBottom: 3 }} size={{ xs: 12, md: 3 }}>
            <Button
              fullWidth
              label={t('dictionary.addValue.button')}
              variant={EButtonVariants.contained}
              onClick={() => navigate(ERouteLinks.AddDictionaryValue.replace(':type', type))}
              sx={{ mt: { xs: 1, md: 3.5 } }}
            />
          </Grid>
        </Grid>
      )}

      {dictionaryAlert && (
        <Alert text={dictionaryAlert.text} variant={dictionaryAlert.variant} />
      )}
      {nextPageAlert && <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />}
      {(dictionaryAlert || nextPageAlert) && <Grid sx={{ mb: 2 }} />}
      {isDesktop ? (
        <Table
          columns={dictionaryViewTableColumns({
            t,
            handleEdit: (idValue: string) =>
              navigate(
                ERouteLinks.EditDictionaryValue.replace(':id', idValue).replace(
                  ':type',
                  type ?? 'null',
                ),
              ),
          })}
          dataSource={mapDictionaryViewToDataSource(dictionaryValues)}
          onSort={setSort}
        />
      ) : (
        <CardList
          items={mapDictionaryViewToDataSource(dictionaryValues)}
          render={(data, index) => (
            <DictionaryViewTableCard data={data} type={type} key={index} />
          )}
        />
      )}

      {paginator && (
        <Pagination
          count={paginator?.totalElements}
          onChangePage={(page) => setCurrentPage(page)}
          current={currentPage}
          perPage={perPage}
          onChangePerPage={(value) => setPerPage(value)}
          isLoading={isPaginationLoading}
        />
      )}
    </>
  );
};

export default ViewDictionaryPage;
